import { Component, OnInit, Input} from '@angular/core';
import { DatePipe } from "@angular/common";
//import { GridOptions, ColDef } from "@ag-grid-enterprise/all-modules"
import { GridOptions, ColDef } from "ag-grid-community";

import { Observable, of, forkJoin } from "rxjs";
import { tap, switchMap } from "rxjs/operators";

import { TabComponent } from "../../../../common/components/tabs/tab.component";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { RouterLinkRendererComponent } from '../../../../common/components/ag-grid/router-link-renderer.component';
import { ModalInfoComponent } from "../../../../common/components/ag-grid/modal-info.component";
import { LandownerProgramSearchSpec, LandownerProgramQueryResult, LandownerProgramStatus, LandownerProgramEvents } from "../../../../common/models/permit.model";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { PermitService } from "../../../../common/services/permit.service";
import { AuthService } from "../../../../services/auth.service";
import { LunaClaimValues, LunaClaimTypes } from '../../../../models/user-identity.model';
import { LandownerPassthroughView } from '../../../models/dashboard.model';




@Component({
    selector: 'landowner-passthrough-view',
    templateUrl: './landowner-passthrough-view.component.html',
    styleUrls: ['./landowner-passthrough-view.component.scss'],
    standalone: false
})
export class LandownerPassthroughViewComponent implements OnInit {
    @Input() widgetTitle: string;


    active$: Observable<LandownerProgramQueryResult[]> = of([]);
    activeGridOptions: GridOptions;

    activeColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerName: "Application #",
            headerTooltip: "Application #",
            field: "applicationId",
            tooltipField: "applicationId",          
            cellRendererSelector: (params) => {
                if (this.hasNecessaryPermission(LunaClaimValues.LandOwnerPassThroughProgram)) {
                    return {
                        component: RouterLinkRendererComponent,
                        params: {
                            url: '/admin/landowner-passthrough/complete',
                            idField: 'applicationId',
                            textField: 'applicationId'
                        }
                    }
                } else {
                    return {
                        component: ModalInfoComponent,
                        params: {
                            modalText: (data) => { return "You do not have permission to perform this action."; },
                            text: (data) => { return data.applicationId; }
                        }
                    }
                }
            },
            minWidth: 140,
            maxWidth: 150
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "API #",
            headerTooltip: "API #",
            field: "wellId",
            tooltipField: "wellId",
            minWidth: 125,
            maxWidth: 125,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Name",
            headerTooltip: "Well Name",
            field: "passthroughProgramWell.wellName",
            tooltipField: "passthroughProgramWell.wellName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Number",
            headerTooltip: "Well Number",
            field: "passthroughProgramWell.wellNumber",
            tooltipField: "passthroughProgramWell.wellNumber",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Status",
            headerTooltip: "Well Status",
            valueGetter: this.formatStatus(),
            tooltipValueGetter: this.formatStatus(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Date Application Submitted",
            headerTooltip: "Date Application Submitted",
            valueGetter: this.getEventDate(LandownerProgramEvents.Submitted, false),
            valueFormatter: this.getEventDate(LandownerProgramEvents.Submitted, true),
            tooltipValueGetter: this.getEventDate(LandownerProgramEvents.Submitted, true),
            filter: "agDateColumnFilter",
            filterParams: this.formatter.defaultDateFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Date Permit Approved",
            headerTooltip: "Date Permit Approved",
            valueGetter: this.getEventDate(LandownerProgramEvents.Approved, false),
            valueFormatter: this.getEventDate(LandownerProgramEvents.Approved, true),
            tooltipValueGetter: this.getEventDate(LandownerProgramEvents.Approved, true),
            filter: "agDateColumnFilter",
            filterParams: this.formatter.defaultDateFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Land Owner Name",
            headerTooltip: "Land Owner Name",
            field: "passthroughProgramLandowner.ownerName",
            tooltipField: "passthroughProgramLandowner.ownerName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250,
            maxWidth: 300
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Plugging Estimate",
            headerTooltip: "Plugging Estimate",
            field: "contractorEstimate",
            tooltipValueGetter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            valueFormatter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            filter: "agNumberColumnFilter",
            filterParams: this.formatter.defaultNumberFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "County",
            headerTooltip: "County",
            field: "passthroughProgramWell.county",
            tooltipField: "passthroughProgramWell.county",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Township",
            headerTooltip: "Township",
            field: "passthroughProgramWell.township",
            tooltipField: "passthroughProgramWell.township",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Section",
            headerTooltip: "Section",
            field: "passthroughProgramWell.section",
            tooltipField: "passthroughProgramWell.section",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 110,
            maxWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Lot",
            headerTooltip: "Lot",
            field: "passthroughProgramWell.lot",
            tooltipField: "passthroughProgramWell.lot",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100,
            maxWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Tract",
            headerTooltip: "Tract",
            field: "passthroughProgramWell.tract",
            tooltipField: "passthroughProgramWell.tract",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100,
            maxWidth: 125
        } as ColDef
    ]

    dispositions: any = [];

    excelStyles = this.formatter.excelStyles;

    exited$: Observable<LandownerProgramQueryResult[]> = of([]);

    exitedColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerName: "Application #",
            headerTooltip: "Application #",
            field: "applicationId",
            tooltipField: "applicationId",
            minWidth: 140,
            maxWidth: 140
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Program Status",
            headerTooltip: "Program Status",
            field: "programStatus",
            tooltipField: "programStatus",
            minWidth: 125,
            maxWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "API #",
            headerTooltip: "API #",
            field: "wellId",
            tooltipField: "wellId",
            minWidth: 125,
            maxWidth: 125,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Name",
            headerTooltip: "Well Name",
            field: "passthroughProgramWell.wellName",
            tooltipField: "passthroughProgramWell.wellName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Number",
            headerTooltip: "Well Number",
            field: "passthroughProgramWell.wellNumber",
            tooltipField: "passthroughProgramWell.wellNumber",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Status",
            headerTooltip: "Well Status",
            valueGetter: this.formatStatus(),
            tooltipValueGetter: this.formatStatus(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Disposition",
            headerTooltip: "Disposition",
            valueGetter: this.formatDisposition(),
            tooltipValueGetter: this.formatDisposition(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Date Application Submitted",
            headerTooltip: "Date Application Submitted",
            valueGetter: this.getEventDate(LandownerProgramEvents.Submitted, false),
            valueFormatter: this.getEventDate(LandownerProgramEvents.Submitted, true),
            tooltipValueGetter: this.getEventDate(LandownerProgramEvents.Submitted, true),
            filter: "agDateColumnFilter",
            filterParams: this.formatter.defaultDateFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Date Permit Approved",
            headerTooltip: "Date Permit Approved",
            valueGetter: this.getEventDate(LandownerProgramEvents.Approved, false),
            valueFormatter: this.getEventDate(LandownerProgramEvents.Approved, true),
            tooltipValueGetter: this.getEventDate(LandownerProgramEvents.Approved, true),
            filter: "agDateColumnFilter",
            filterParams: this.formatter.defaultDateFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Land Owner Name",
            headerTooltip: "Land Owner Name",
            field: "passthroughProgramLandowner.ownerName",
            tooltipField: "passthroughProgramLandowner.ownerName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250,
            maxWidth: 275
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Contractor Name",
            headerTooltip: "Contractor Name",
            field: "passthroughProgramContractor.contractorName",
            tooltipField: "passthroughProgramContractor.contractorName",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250,
            maxWidth: 275
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Plugging Estimate",
            headerTooltip: "Plugging Estimate",
            field: "contractorEstimate",
            tooltipValueGetter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            valueFormatter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            filter: "agNumberColumnFilter",
            filterParams: this.formatter.defaultNumberFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "County",
            headerTooltip: "County",
            field: "passthroughProgramWell.county",
            tooltipField: "passthroughProgramWell.county",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Township",
            headerTooltip: "Township",
            field: "passthroughProgramWell.township",
            tooltipField: "passthroughProgramWell.township",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Section",
            headerTooltip: "Section",
            field: "passthroughProgramWell.section",
            tooltipField: "passthroughProgramWell.section",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 110,
            maxWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Lot",
            headerTooltip: "Lot",
            field: "passthroughProgramWell.lot",
            tooltipField: "passthroughProgramWell.lot",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100,
            maxWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Tract",
            headerTooltip: "Tract",
            field: "passthroughProgramWell.tract",
            tooltipField: "passthroughProgramWell.tract",
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100,
            maxWidth: 125
        } as ColDef
    ];

    exitedGridOptions: GridOptions;



    notSubmittedColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerName: "Application #",
            headerTooltip: "Application #",
            field: "applicationId",
            tooltipField: "applicationId",            
            cellRendererSelector: (params) => {
                if (this.hasNecessaryPermission(LunaClaimValues.LandOwnerPassThroughProgram)) {
                    return {
                        component: RouterLinkRendererComponent,
                        params: {
                            url: '/admin/landowner-passthrough/edit',
                            idField: 'applicationId',
                            textField: 'applicationId'
                        }
                    }
                } else {
                    return {
                        component: ModalInfoComponent,
                        params: {
                            modalText: (data: any) => { return "You do not have permission to perform this action."; },
                            text: (data: any) => { return data.applicationId; }
                        }
                    }
                }
            },
            minWidth: 145,
            maxWidth: 145
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "API #",
            headerTooltip: "API #",
            field: "wellId",
            tooltipField: "wellId",
            minWidth: 125,
            maxWidth: 125,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Name",
            headerTooltip: "Well Name",
            valueGetter: this.formatField("passthroughProgramWell.wellName", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.wellName", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Number",
            headerTooltip: "Well Number",
            valueGetter: this.formatField("passthroughProgramWell.wellNumber", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.wellNumber", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Status",
            headerTooltip: "Well Status",
            valueGetter: this.formatStatus(),
            tooltipValueGetter: this.formatStatus(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Disposition",
            headerTooltip: "Disposition",
            valueGetter: this.formatDisposition(),
            tooltipValueGetter: this.formatDisposition(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Land Owner Name",
            headerTooltip: "Land Owner Name",
            valueGetter: this.formatField("passthroughProgramLandowner.ownerName", ""),
            tooltipValueGetter: this.formatField("passthroughProgramLandowner.ownerName", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250,
            maxWidth: 275
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Contractor Name",
            headerTooltip: "Contractor Name",
            valueGetter: this.formatField("passthroughProgramContractor.contractorName", ""),
            tooltipValueGetter: this.formatField("passthroughProgramContractor.contractorName", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250,
            maxWidth: 275
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Plugging Estimate",
            headerTooltip: "Plugging Estimate",
            field: "contractorEstimate",
            tooltipValueGetter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            valueFormatter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            filter: "agNumberColumnFilter",
            filterParams: this.formatter.defaultNumberFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "County",
            headerTooltip: "County",
            valueGetter: this.formatField("passthroughProgramWell.county", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.county", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Township",
            headerTooltip: "Township",
            valueGetter: this.formatField("passthroughProgramWell.township", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.township", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Section",
            headerTooltip: "Section",
            valueGetter: this.formatField("passthroughProgramWell.section", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.section", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 110,
            maxWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Lot",
            headerTooltip: "Lot",
            valueGetter: this.formatField("passthroughProgramWell.lot", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.lot", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100,
            maxWidth: 125
        } as ColDef
    ];

    notSubmitted$: Observable<LandownerProgramQueryResult[]>;
    notSubmittedGridOptions: GridOptions;

    pending$: Observable<LandownerProgramQueryResult[]> = of([]);
    pendingGridOptions: GridOptions;
    pendingColumns: ColDef[] = [
        {
            headerClass: "wrapped",
            headerName: "Application #",
            headerTooltip: "Application #",
            field: "applicationId",
            tooltipField: "applicationId",
            cellRendererSelector: (params) => {
                if (this.hasNecessaryPermission(LunaClaimValues.ApproveLandownerPassthroughProgramApplication)) {
                    return {
                        component: RouterLinkRendererComponent,
                        params: {
                            url: '/admin/landowner-passthrough/review',
                            idField: 'applicationId',
                            textField: 'applicationId'
                        }
                    }
                } else {
                    return {
                        component: ModalInfoComponent,
                        params: {
                            modalText: (data: any) => { return "You do not have permission to perform this action."; },
                            text: (data: any) => { return data.applicationId; }
                        }
                    }
                }
            },
            minWidth: 140,
            maxWidth: 140
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "API #",
            headerTooltip: "API #",
            field: "wellId",
            tooltipField: "wellId",
            minWidth: 125,
            maxWidth: 125,
            cellClass: "stringType"
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Name",
            headerTooltip: "Well Name",
            valueGetter: this.formatField("passthroughProgramWell.wellName", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.wellName", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Number",
            headerTooltip: "Well Number",
            valueGetter: this.formatField("passthroughProgramWell.wellNumber", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.wellNumber", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Well Status",
            headerTooltip: "Well Status",
            valueGetter: this.formatStatus(),
            tooltipValueGetter: this.formatStatus(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Disposition",
            headerTooltip: "Disposition",
            valueGetter: this.formatDisposition(),
            tooltipValueGetter: this.formatDisposition(),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            cellClass: "stringType",
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Land Owner Name",
            headerTooltip: "Land Owner Name",
            valueGetter: this.formatField("passthroughProgramLandowner.ownerName", ""),
            tooltipValueGetter: this.formatField("passthroughProgramLandowner.ownerName", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250,
            maxWidth: 275
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Contractor Name",
            headerTooltip: "Contractor Name",
            valueGetter: this.formatField("passthroughProgramContractor.contractorName", ""),
            tooltipValueGetter: this.formatField("passthroughProgramContractor.contractorName", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 250,
            maxWidth: 275
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Plugging Estimate",
            headerTooltip: "Plugging Estimate",
            field: "contractorEstimate",
            tooltipValueGetter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            valueFormatter: this.formatter.formatCurrency("contractorEstimate", "USD"),
            filter: "agNumberColumnFilter",
            filterParams: this.formatter.defaultNumberFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "County",
            headerTooltip: "County",
            valueGetter: this.formatField("passthroughProgramWell.county", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.county", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Township",
            headerTooltip: "Township",
            valueGetter: this.formatField("passthroughProgramWell.township", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.township", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 150,
            maxWidth: 175
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Section",
            headerTooltip: "Section",
            valueGetter: this.formatField("passthroughProgramWell.section", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.section", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 110,
            maxWidth: 125
        } as ColDef,
        {
            headerClass: "wrapped",
            headerName: "Lot",
            headerTooltip: "Lot",
            valueGetter: this.formatField("passthroughProgramWell.lot", ""),
            tooltipValueGetter: this.formatField("passthroughProgramWell.lot", ""),
            filter: "agTextColumnFilter",
            filterParams: this.formatter.defaultTextFilterParams,
            minWidth: 100,
            maxWidth: 125
        } as ColDef
    ]

    programStatuses = LandownerProgramStatus;

    returnedGridOptions: GridOptions;
    returned$: Observable<LandownerProgramQueryResult[]>;

    _selectedTab: LandownerProgramStatus = LandownerProgramStatus.InProcess;

    get selectedTab() : LandownerProgramStatus {
        return this._selectedTab;
    }

    set selectedTab(value: LandownerProgramStatus) {
        this._selectedTab = value;
    }

    statuses: any = [];


    constructor(private formatter: GridFormatterService, private domainService: DomainDefinitionService, private permitService: PermitService, private authService: AuthService, private pipe: DatePipe) {
    }

    ngOnInit() {
        this.configureGridOptions();
        this.notSubmitted$ = this.getData([LandownerProgramStatus.InProcess]);
    }


    private configureGridOptions(): void {
        this.notSubmittedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.notSubmittedColumns, false, true, true, true);
        this.notSubmittedGridOptions.headerHeight = 50;
        this.pendingGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.pendingColumns, false, true, true, true);
        this.pendingGridOptions.headerHeight = 50;
        this.returnedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.notSubmittedColumns, false, true, true, true);
        this.returnedGridOptions.headerHeight = 50;
        this.activeGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.activeColumns, false, true, true, true);
        this.activeGridOptions.headerHeight = 50;
        this.exitedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.exitedColumns, false, true, true, true);
        this.exitedGridOptions.headerHeight = 50;
        this.activeGridOptions.defaultColDef = { filter: true, resizable: true, sortable: true, editable: false } as ColDef;
        this.returnedGridOptions.defaultColDef = { filter: true, resizable: true, sortable: true, editable: false } as ColDef;
        this.pendingGridOptions.defaultColDef = { filter: true, resizable: true, sortable: true, editable: false } as ColDef;
        this.notSubmittedGridOptions.defaultColDef = { filter: true, resizable: true, sortable: true, editable: false } as ColDef;
        this.exitedGridOptions.defaultColDef = { filter: true, resizable: true, sortable: true, editable: false } as ColDef;
    }

    formatDisposition(): any {
        return (obj: any) => {
            let value = "";
            if (obj.data && obj.data.wellDisposition != null) {
                value = obj.data.wellDisposition;
                let definition = this.dispositions.find(s => s.Code == value);
                if (definition != null) {
                    value = definition.Definition;
                }
            }
            return value;
        }
    }

    formatField(fieldName: string, defaultValue: string = ""): any {
        return (obj: any) => {
            let value = defaultValue;
            let fields = fieldName.split('.');
            
            if (obj != null && fields.length > 0) {
                let objData: any = obj.data;;
                fields.forEach(f => {
                    if (objData == null) return;
                    objData = objData[f];
                });
                value = objData != null && objData.length > 0 ? objData : defaultValue;
            }
            return value;
        }
    }

    formatStatus(): any {
        return (obj: any) => {
            let value = "";
            if (obj.data && obj.data.wellStatus != null) {
                value = obj.data.wellStatus;
                let definition = this.statuses.find(s => s.Code == value);
                if (definition != null) {
                    value = definition.Definition;
                }
            }
            return value;
        }
    }

    getData(statuses: LandownerProgramStatus[]): Observable<LandownerProgramQueryResult[]> {
        let statusObs$: Observable<any> = (this.statuses == null || this.statuses.length == 0)
            ? this.domainService.getValues("WL_STATUS").pipe(
                tap((data: any) => {
                    this.statuses = data;
                })
            )
            : of(this.statuses);

        let descriptionObs$: Observable<any> = (this.dispositions == null || this.dispositions.length == 0)
            ? this.domainService.getValues("STATUS_DESC").pipe(tap((data: any) => {
                this.dispositions = data;
            })
            )
            : of(this.dispositions);

        let spec = {
            apiNumbers: [],
            programStatus: statuses,
            landowner: "",
            contractor: "",
            includeStatusAndDisposition: true
        } as LandownerProgramSearchSpec;

        const dataObs$: Observable<LandownerProgramQueryResult[]> = this.permitService.searchLandownerPassthroughApplications(spec);

        return forkJoin([statusObs$, descriptionObs$]).pipe(
            switchMap(() => {
                return dataObs$;
            })
        );
    }

    getEventDate(event: LandownerProgramEvents, formatAsString: boolean): any {
        return (obj: any) => {
            let value = "N/A";
            if (obj.data != null) {
                if (obj.data.landownerPassthroughProgramHistories != null && obj.data.landownerPassthroughProgramHistories.length > 0) {
                    let submissions = obj.data.landownerPassthroughProgramHistories.filter(h => h.eventTypeDescription === event)
                    if (submissions != null && submissions.length > 0) {
                        submissions = submissions.sort((a, b) => {
                            if (a.recordedDate < b.recordedDate) return -1;
                            if (a.recordedDate > b.recordedDate) return 1;
                            return 0;
                        });
                        value = submissions[0].recordedDate;
                    } else {
                        if (event === LandownerProgramEvents.Approved) {
                            value = obj.data.lastUpdated;
                        } else {
                            value = obj.data.dateCreated;
                        }
                    }

                }
                else  {
                    if (event === LandownerProgramEvents.Approved) {
                        value = obj.data.lastUpdated;
                    } else {
                        value = obj.data.dateCreated;
                    }
                }

            }
            if (value != null && value.length > 0 && formatAsString) value = this.pipe.transform(value, "MM/dd/yyyy hh:mm a");
            return value;
        }
    }


    hasNecessaryPermission(permission: string): boolean {
       return this.authService.User.hasPermission(LunaClaimTypes.System, permission)
    }

    onTabClicked(tab: TabComponent): void {
        switch (tab.title) {
            case "Awaiting Review":
                this.selectedTab = LandownerProgramStatus.AwaitingReview;
                this.pending$ = this.getData([LandownerProgramStatus.AwaitingReview]);
                break;
            case "Not Submitted":
                this.selectedTab = LandownerProgramStatus.InProcess;
                this.notSubmitted$ = this.getData([LandownerProgramStatus.InProcess]);
                break;
            case "Returned For Correction":
                this.selectedTab = LandownerProgramStatus.ReturnedForCorrection;
                this.returned$ = this.getData([LandownerProgramStatus.ReturnedForCorrection])
                break;
            case "Active Wells":
                this.selectedTab = LandownerProgramStatus.Active;
                this.active$ = this.getData([LandownerProgramStatus.Active]);
                break;
            case "Exited Wells":
                this.selectedTab = LandownerProgramStatus.Plugged;
                this.exited$ = this.getData([LandownerProgramStatus.Plugged, LandownerProgramStatus.Withdrawn]);
                break;
        }
    }

}
